<!-- {{ config() | json }} -->

@if (config(); as config) {
    <div class="border-bottom border-3" [style.--bs-border-color]="'var(--bs-gray-400)'"></div>
    <div class="container">
        <div
            class="d-flex justify-content-center justify-content-sm-between align-items-center flex-wrap my-3 pb-4 border-bottom border-1"
            [style.--bs-border-color]="'var(--bs-gray-200)'">
            @if (config.utilitySettings.logo) {
                <img src="{{ config.utilitySettings.logo }}" alt="Logo" class="mb-3 mb-md-0 company-logo" />
            } @else {
                <h3 class="mb-0">{{ config.utilitySettings.companyName }}</h3>
            }
            <div class="d-none d-sm-block">
                <div class="d-flex flex-column mb-0 text-center text-md-end mt-3 mt-md-0">
                    <p class="mb-0">
                        <b>{{ config.utilitySettings.companyName }}</b>
                    </p>
                    @if (config.utilitySettings.phoneNumber) {
                        <a href="tel:{{ config.utilitySettings.phoneNumber }}">{{
                            config.utilitySettings.phoneNumber
                        }}</a>
                    }
                    @if (config.utilitySettings.emailAddress) {
                        <a href="mailto:{{ config.utilitySettings.emailAddress }}">{{
                            config.utilitySettings.emailAddress
                        }}</a>
                    }
                </div>
            </div>
        </div>
    </div>
}

@if (smartQuote(); as quote) {
    <div class="page-content">
        <div class="container my-4">
            <div class="row">
                <div class="col-12"></div>
            </div>
            <div class="row">
                <div class="col-lg-7">
                    <h1>Your Quotation</h1>

                    @if (!awaitingResponse()) {
                        <div class="col-12">
                            <ngb-alert
                                [type]="quote.isAccepted ? 'success' : 'danger'"
                                [dismissible]="false"
                                class="my-3">
                                <strong>Quote {{ quote.isAccepted ? 'Accepted' : 'Declined' }}!</strong><br />
                                @if (quote.isAccepted) {
                                    Thank you for accepting the quotation.
                                } @else {
                                    We are sorry to hear that you have declined the quotation.
                                }
                            </ngb-alert>
                        </div>
                    }

                    <div class="d-flex flex-row flex-wrap justify-content-between">
                        <div>
                            <p class="mb-1 text-body-secondary">Quotation No.</p>
                            <p class="fw-bold">#{{ quote.id }}</p>
                        </div>
                        <div>
                            <p class="mb-1 text-body-secondary">Created On</p>
                            <p class="fw-bold">{{ quote.preparedDate | date }}</p>
                        </div>
                        <div>
                            <p class="mb-1 text-body-secondary">Prepared By</p>
                            <p class="fw-bold">{{ quote.preparedBy }}</p>
                        </div>
                    </div>

                    <p class="mb-1 text-body-secondary">Your Address</p>
                    <p class="fw-bold">
                        {{ quote.address.fullAddressAsSingleLine }}
                    </p>

                    <div class="card mt-4">
                        <div class="card-body">
                            <p class="fs-4 fw-bold text-body-secondary">
                                Dear {{ quote.customerName || 'Customer' }},<br />
                            </p>

                            <div class="row">
                                <div
                                    [ngClass]="{ 'col-sm-7': quote.defaultImageUrl, 'col-12': !quote.defaultImageUrl }"
                                    class="col-sm-7">
                                    <p>Please review this quote and click 'accept' if you agree with the terms.</p>

                                    @if (quote.jobDescription) {
                                        <h5 class="text-body-secondary">Job Description</h5>
                                        <p style="white-space: pre-line">{{ quote.jobDescription }}</p>
                                    }
                                </div>
                                @if (quote.defaultImageUrl) {
                                    <div class="col-sm-5">
                                        <img
                                            src="{{ quote.defaultImageUrl }}"
                                            alt="Quote Image"
                                            class="img-fluid mb-3" />
                                    </div>
                                }
                            </div>
                        </div>
                    </div>

                    @if (quote.items.length && !quote.combinedLines) {
                        <div class="card mt-4">
                            <div class="card-body">
                                <h5 class="text-body-secondary mb-3">Parts</h5>
                                @for (product of quote.items; track product; let last = $last) {
                                    <div class="d-flex align-items-center pb-2 mb-2 border-bottom">
                                        <div>
                                            <p class="fw-bold mb-1">
                                                {{ product.quantity }} x {{ product.description }}
                                            </p>
                                            <p class="mb-0 small text-body-secondary">
                                                {{ product.unitCost | currency }} unit
                                            </p>
                                        </div>
                                    </div>
                                }

                                <div class="d-flex align-items-center mt-4">
                                    <p class="mb-0 fw-bold fs-5">Parts Cost</p>
                                    <p class="ms-auto mb-0 fs-3 fw-bold text-body-secondary">
                                        {{ quote.partsCost | currency }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    }
                </div>
                <div class="col-lg-4 offset-lg-1">
                    <div class="card bg-body-tertiary mt-3 mt-lg-0">
                        <div class="card-body">
                            <h5 class="card-title">Quote Summary</h5>

                            @if (quote.combinedLines) {
                                <div class="d-flex justify-content-between">
                                    <span>Parts & Labour</span>
                                    <span class="fw-bold">{{ quote.partsCost + quote.labourCost | currency }}</span>
                                </div>
                            } @else {
                                <div class="d-flex justify-content-between">
                                    <span>Parts</span>
                                    <span class="fw-bold">{{ quote.partsCost | currency }}</span>
                                </div>
                                <div class="d-flex justify-content-between">
                                    <span>Labour</span>
                                    <span class="fw-bold">{{ quote.labourCost | currency }}</span>
                                </div>
                            }
                            @if (quote.discount) {
                                <div class="d-flex justify-content-between fw-bold text-success">
                                    <span>Discount</span>
                                    <span class="fw-bold">{{ quote.discount * -1 | currency }}</span>
                                </div>
                            }
                            <div class="d-flex justify-content-between">
                                <span>Subtotal</span>
                                <span class="fw-bold">{{ quote.subTotal | currency }}</span>
                            </div>
                            <div class="d-flex justify-content-between">
                                <span>VAT</span>
                                <span class="fw-bold">{{ quote.vat | currency }}</span>
                            </div>

                            <div class="d-flex justify-content-between fw-bold fs-4 mt-3 border-top pt-3">
                                <span>Total</span>
                                <span class="fw-bold">{{ quote.totalAmount | currency }}</span>
                            </div>

                            @if (!quote.isAccepted) {
                                <p class="mt-4 mb-0 small text-body-secondary">
                                    @if (quote.termsFileUrl) {
                                        By accepting this quote, you agree to the following
                                        <a target="_blank" [href]="quote.termsFileUrl">terms</a>.
                                    } @else {
                                        By accepting this quote, you agree to the terms and conditions of the service.
                                    }
                                </p>
                            }

                            <div class="mt-3 d-grid gap-2">
                                <button
                                    sfButton
                                    type="button"
                                    color="success"
                                    [disabled]="!awaitingResponse()"
                                    (click)="acceptQuote()">
                                    {{ quote.isAccepted ? 'Quote Accepted' : 'Accept Quote' }}
                                </button>
                                <button sfButton type="button" variant="outline" color="dark" (click)="downloadPdf()">
                                    Download PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-lg-7">
                    @if (config(); as config) {
                        <div class="card mt-4">
                            <div class="card-body">
                                <h5 class="card-title text-body-secondary">Get in Touch</h5>
                                <p>Have a question about your quotation?</p>
                                <div class="d-flex flex-column gap-1 justify-content-center small">
                                    @if (config.utilitySettings.phoneNumber) {
                                        <span
                                            >Tel:
                                            <a href="tel:{{ config.utilitySettings.phoneNumber }}">{{
                                                config.utilitySettings.phoneNumber
                                            }}</a></span
                                        >
                                    }
                                    @if (config.utilitySettings.emailAddress) {
                                        <span
                                            >Email:
                                            <a href="mailto:{{ config.utilitySettings.emailAddress }}">{{
                                                config.utilitySettings.emailAddress
                                            }}</a></span
                                        >
                                    }
                                    @if (config.utilitySettings.address.fullAddressAsSingleLine) {
                                        <span
                                            >Address: {{ config.utilitySettings.address.fullAddressAsSingleLine }}</span
                                        >
                                    }
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    </div>
}

<div
    class="d-flex flex-column justify-content-center align-items-center bg-surefire-blue mt-4 py-5 text-center text-light">
    <p class="mb-0">
        Powered by
        <a class="text-surefire-yellow fw-bold" target="_blank" href="https://surefiresoftware.co.uk"
            >Surefire Software</a
        >
    </p>
    <a target="_blank" href="https://surefiresoftware.co.uk">
        <img src="/assets/images/surefire-logo-alt.svg" alt="Surefire Software Logo" class="img-fluid mt-3" />
    </a>
</div>

<ng-template #acceptedModal let-modal>
    <div class="modal-header">
        <h4 class="modal-title text-success" id="modal-basic-title">Quote Accepted</h4>
        <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
    </div>
    <div class="modal-body">
        <p class="mb-0">Thank you for accepting the quote.</p>
    </div>
    <div class="modal-footer">
        <button sfButton type="button" variant="outline" color="dark" (click)="modal.close('Save click')">Close</button>
    </div>
</ng-template>
